/**
 * Single chokepoint that every outbound send must pass through.
 *
 * Each gate returns { ok, code, reason } so the worker can log structured
 * suppression reasons. NEVER short-circuit any of these checks at call sites —
 * always go through `evaluateSend`.
 */

import { isWithinSendWindow, DEFAULT_QUIET_HOURS, type QuietHoursPolicy } from "./quietHours";

export type Channel = "sms" | "whatsapp";

export interface ContactSnapshot {
  id: string;
  phoneE164: string;
  language: "en" | "es";
  timezone?: string;
  isLandline?: boolean;
  isReassignedRisk?: boolean;
  consentBySource: Record<string, { channel: Channel; status: "granted" | "revoked"; at: string; disclosureVersion: string }>;
  suppressed: boolean;
  suppressionReason?: string;
}

export interface CampaignSnapshot {
  id: string;
  channel: Channel;
  senderEligible: boolean;          // e.g. 10DLC campaign approved, WhatsApp template approved
  campaignRegistered: boolean;       // TCR / Meta business verified
  requiresConsent: boolean;
  quietHours?: QuietHoursPolicy;
  organizationId: string;
}

export interface PolicyResult {
  ok: boolean;
  code:
    | "OK"
    | "SUPPRESSED"
    | "NO_CONSENT"
    | "CONSENT_REVOKED"
    | "QUIET_HOURS"
    | "SENDER_NOT_ELIGIBLE"
    | "CAMPAIGN_NOT_REGISTERED"
    | "LANDLINE"
    | "REASSIGNED_RISK"
    | "DUPLICATE";
  reason?: string;
}

export function evaluateSend(args: {
  contact: ContactSnapshot;
  campaign: CampaignSnapshot;
  now?: Date;
  recentSendsToContactInCampaign?: number;
}): PolicyResult {
  const { contact, campaign, now = new Date() } = args;

  if (!campaign.campaignRegistered) {
    return { ok: false, code: "CAMPAIGN_NOT_REGISTERED", reason: "Campaign not registered with carrier/platform" };
  }
  if (!campaign.senderEligible) {
    return { ok: false, code: "SENDER_NOT_ELIGIBLE", reason: "Sender not approved for this channel/use-case" };
  }
  if (contact.suppressed) {
    return { ok: false, code: "SUPPRESSED", reason: contact.suppressionReason ?? "Contact on suppression list" };
  }
  if (contact.isLandline && campaign.channel === "sms") {
    return { ok: false, code: "LANDLINE", reason: "Number is landline" };
  }
  if (contact.isReassignedRisk) {
    return { ok: false, code: "REASSIGNED_RISK", reason: "Number flagged as reassigned; revalidate consent" };
  }

  if (campaign.requiresConsent) {
    const consent = Object.values(contact.consentBySource).find(
      (c) => c.channel === campaign.channel,
    );
    if (!consent) {
      return { ok: false, code: "NO_CONSENT", reason: `No ${campaign.channel} consent on file` };
    }
    if (consent.status === "revoked") {
      return { ok: false, code: "CONSENT_REVOKED", reason: `Consent revoked at ${consent.at}` };
    }
  }

  const window = isWithinSendWindow(
    now,
    campaign.quietHours ?? DEFAULT_QUIET_HOURS,
    contact.timezone,
  );
  if (!window.allowed) {
    return { ok: false, code: "QUIET_HOURS", reason: window.reason };
  }

  if ((args.recentSendsToContactInCampaign ?? 0) > 0) {
    return { ok: false, code: "DUPLICATE", reason: "Already sent this campaign step to this contact" };
  }

  return { ok: true, code: "OK" };
}
